import React, { useState, useMemo } from "react";
import styles from "./monthsHighestDelinquencyTable.module.css";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import { addPorcentage } from "../../../utils/formatter";

const MonthsHighestDelinquencyTable = ({ delinquencyData = [], showAll = false }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showPopover, setShowPopover] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  const validDelinquencyData = useMemo(() => {
    return delinquencyData
      .filter((item) =>
        (item.resultado || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const cmp = a.percentualInad - b.percentualInad;
        return sortOrder === "asc" ? cmp : -cmp;
      });
  }, [delinquencyData, searchTerm, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(validDelinquencyData.length / itemsPerPage),
    [validDelinquencyData.length, itemsPerPage]
  );

  const currentTableData = useMemo(() => {
    if (showAll) return validDelinquencyData;
    const start = (currentPage - 1) * itemsPerPage;
    return validDelinquencyData.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, validDelinquencyData, showAll]);

  const handlePageClick = (page) => setCurrentPage(page);

  const emptyRows = showAll ? [] : Array(Math.max(0, itemsPerPage - currentTableData.length)).fill(null);
  const hasNoData = validDelinquencyData.length === 0;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Meses com Maior Inadimplência</h2>
          <div style={{ position: "relative" }}>
            <img
              src={InfoIcon}
              alt="Informação"
              onClick={() => setShowPopover(!showPopover)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                verticalAlign: "middle",
                display: "inline-block",
              }}
            />
            {showPopover && (
              <div className={styles.popover}>
                <p style={{ margin: 0, fontSize: "12px" }}>
                  Tabela que exibe os meses com maiores índices de inadimplência, permitindo identificar períodos críticos de não pagamento. Os dados podem ser filtrados por mês/ano e ordenados por percentual.
                </p>
                <button
                  onClick={() => setShowPopover(false)}
                  className={styles.closeBtn}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          <div className={styles.searchWrapper}>
            <img src={SearchIcon} alt="Buscar" className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Pesquisar mês..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
            />
          </div>

          <select
            className={styles.orderWrapper}
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="desc">Maior Inadimplência</option>
            <option value="asc">Menor Inadimplência</option>
          </select>
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Mês/Ano</th>
            <th>Percentual de Inadimplência</th>
          </tr>
        </thead>
        <tbody>
          {hasNoData ? (
            <tr>
              <td colSpan="2" className={styles.noData}>
                Nenhum dado de inadimplência encontrado
              </td>
            </tr>
          ) : (
            <>
              {currentTableData.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.resultado}</td>
                  <td>{addPorcentage(item.percentualInad)}</td>
                </tr>
              ))}
              {emptyRows.map((_, idx) => (
                <tr key={`empty-${idx}`}>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {!showAll && totalPages > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={page === currentPage ? styles.activePage : ""}
              onClick={() => handlePageClick(page)}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default MonthsHighestDelinquencyTable;
